/**
 * „Texte prüfen und übersetzen“ (S61): how far each text of the business is, for every target at once.
 * Checking the German is a target like a language (Martin, 01.11.2026: „Deutsch unkorrigiert nach
 * Deutsch geprüft ist sehr ähnlich wie eine Sprachübersetzung“): „de“ is the German as checked, the
 * others the languages switched on. Each text has one of four states per target:
 *
 *   open    German: not checked yet (or changed by hand since); a language: no translation, or half one
 *   review  German: corrections open; a language: by the model, not looked through yet
 *   stale   a language: by hand or looked through, made from an older German text (dishes only; the
 *           dictionary keys by the text); the model's own of an older text is open, it makes it anew
 *   ok      German: checked, every correction decided; a language: by hand, looked through, the base
 *           vocabulary's or the regulation's
 *
 * One function counts for every place that shows it (the button in step 2, step 3, „Im Netz“, the
 * lists, the review page), so they never disagree.
 */
import type { Allergen } from "./allergens.ts";
import { isDietTag } from "./dishes.ts";
import type { Dish } from "./dishes.ts";
import { menuDishes, menuView } from "./menu.ts";
import type { MenuInput } from "./menu.ts";
import { phraseOf } from "./phrase-base.ts";
import { businessPhrases, hasWords, phraseKey, viewPhrases } from "./phrases.ts";
import type { PhraseGroup, Phrases } from "./phrases.ts";
import { cardSpots, dishSpots, proofOf, proofSpots } from "./proofread.ts";
import type { ProofSpot, Proofs } from "./proofread.ts";
import type { CardText } from "./texts.ts";
import { keepsName, translationState } from "./translation.ts";
import type { Lang } from "./translation.ts";

export type TextTarget = "de" | Lang;
export type TextState = "open" | "review" | "stale" | "ok";
/** Where a row comes from: a dish or a drink, else the group of the dictionary (S36). */
export type TextGroup = "dish" | "drink" | PhraseGroup;

export const TEXT_GROUP_LABELS: Record<TextGroup, string> = {
  dish: "Speisen",
  drink: "Getränke",
  card: "Speisekarten",
  category: "Kategorien",
  tag: "Tags",
  allergen: "Allergene und Zusatzstoffe",
  text: "Textbausteine",
  price: "Preisbezeichnungen",
  footer: "Fuß",
};

export interface TextRow {
  /** `dish:<id>`, or `text:<German>` for a text of the dictionary. */
  id: string;
  group: TextGroup;
  /** A dish's row. */
  dish?: Dish;
  /** A text's row: the German text (`phraseKey`). */
  text?: string;
  /** Where „Deutsch prüfen“ looks at it: the dish's name and description, or every place the text stands. */
  spots: ProofSpot[];
  /** The German's state; missing when it is not checked (switched off, or nothing to check). */
  de?: TextState;
  /** Per language switched on, where the row needs a translation. */
  langs: Partial<Record<Lang, TextState>>;
}

/** How many rows of a target are in a state: dishes and drinks apart from texts. */
export interface StateCount {
  dishes: number;
  texts: number;
}
export type TargetCounts = Record<Exclude<TextState, "ok">, StateCount>;

export interface TextStatus {
  rows: TextRow[];
  /** The targets switched on, German first. */
  targets: TextTarget[];
  counts: Partial<Record<TextTarget, TargetCounts>>;
}

/** What the status is made of: the data of the business as the app and the server hold it. */
export interface TextSource {
  dishes: readonly Dish[];
  allergens: readonly Allergen[];
  texts: readonly CardText[];
  categories: readonly string[];
  drinkCategories: readonly string[];
  tags: readonly string[];
  drinkTags: readonly string[];
  menus: readonly MenuInput[];
  tagIcons: Readonly<Record<string, string>>;
  footer: string;
  languages: readonly Lang[];
  proofread: boolean;
  proofs: Proofs;
  phrases: Phrases;
}

/**
 * Whether „Deutsch prüfen“ is on (S61 § 11, Martin 02.11.2026): switched on, or with any language –
 * the German is checked before it is translated, always.
 */
export const proofreadOn = (s: { proofread?: boolean; languages?: readonly Lang[] }) => Boolean(s.proofread) || (s.languages?.length ?? 0) > 0;

/** The whole catalogue, or one card (saved or a draft, with its id). */
export type TextScope = { menu: MenuInput & { id: string } } | null;

/**
 * The words of one card the dictionary translates (S61): what it shows (`viewPhrases`), its footer,
 * and the categories and tags of its dishes – the same as „Deutsch prüfen“ looks at for it
 * (`cardSpots`), so that „Texte fertig machen“ for a card leaves none of them half done (Martin,
 * 01.11.2026: „Tags werden nicht mitübersetzt“). The diets' tags have their own words (S36).
 */
export function cardPhrases(menu: MenuInput, src: Pick<TextSource, "dishes" | "allergens" | "texts" | "tagIcons" | "footer">): Map<string, PhraseGroup> {
  const byId = new Map(src.dishes.map((d) => [d.id, d]));
  const out = new Map<string, PhraseGroup>(viewPhrases(menuView(menu, byId, src.allergens, new Map(src.texts.map((t) => [t.id, t])), src.tagIcons)).map((t) => [t, "card"]));
  const add = (group: PhraseGroup, t: string) => {
    const key = phraseKey(t);
    if (hasWords(key) && !out.has(key)) out.set(key, group);
  };
  for (const id of new Set(menuDishes(menu))) {
    const d = byId.get(id);
    if (!d) continue;
    if (d.category) add("category", d.category);
    for (const t of d.tags) if (!isDietTag(t)) add("tag", t);
  }
  add("footer", src.footer);
  return out;
}

/** Which group a spot of a text belongs to, by its key. */
function groupOf(key: string): PhraseGroup {
  if (key.startsWith("menu:")) return "card";
  if (key.startsWith("category:")) return "category";
  if (key.startsWith("tag:")) return "tag";
  if (key.startsWith("allergen:")) return "allergen";
  if (key.startsWith("text:")) return "text";
  if (key.startsWith("settings:")) return "footer";
  return "price";
}

/** The German's state at some spots: corrections open, not checked, or checked. */
export function germanState(spots: readonly ProofSpot[], proofs: Proofs): TextState | undefined {
  let state: TextState | undefined;
  for (const s of spots) {
    if (!s.text.trim()) continue;
    const p = proofOf(s.text, proofs[s.key]);
    if (p.edits.length) return "review";
    if (!p.checked) state = "open";
    else state ??= "ok";
  }
  return state;
}

/** A dish's state in a language; undefined when it needs none (a name that stays, no description). */
export function dishLangState(d: Dish, lang: Lang): TextState | undefined {
  if (keepsName(d) && d.description === "") return undefined;
  const state = translationState(d, lang);
  const by = d.translations?.[lang]?.by;
  if (state === "none" || state === "missing") return "open";
  // The model's own of an older text it makes anew itself (`toTranslate`); one by hand or looked through someone decides.
  if (state === "stale") return by === "ai" ? "open" : "stale";
  return by === "ai" ? "review" : "ok";
}

/** A text's state in a language; undefined when it has no words to translate („0,3 l“). */
export function phraseLangState(text: string, lang: Lang, phrases: Phrases): TextState | undefined {
  if (!hasWords(text)) return undefined;
  const found = phraseOf(text, lang, phrases);
  if (!found) return "open";
  return found.by === "ai" ? "review" : "ok";
}

/**
 * The rows of a scope with their states. A card: the dishes and texts standing on it, its own texts and
 * those it shows (categories as sections, tags, the legend, the footer); the catalogue: everything.
 */
export function textStatus(src: TextSource, scope: TextScope = null): TextStatus {
  const menus = scope ? [...src.menus.filter((m) => !("id" in m) || (m as { id?: string }).id !== scope.menu.id), scope.menu] : src.menus;
  const all = proofSpots({ ...src, menus: menus as (MenuInput & { id: string })[] });
  let dishes: Dish[];
  let spots: ProofSpot[];
  let words: Map<string, PhraseGroup>;
  if (scope) {
    const on = new Set(menuDishes(scope.menu));
    dishes = src.dishes.filter((d) => on.has(d.id));
    spots = cardSpots(all, scope.menu.id, scope.menu, src.dishes);
    words = cardPhrases(scope.menu, src);
  } else {
    dishes = src.dishes.filter((d) => !d.archived);
    spots = all;
    words = new Map(businessPhrases({ ...src, menus }).map((p) => [p.text, p.group]));
  }
  const langs = src.languages;
  const rows: TextRow[] = [];
  const counted = new Set<string>();
  for (const d of dishes) {
    const own = dishSpots(d);
    for (const s of own) counted.add(s.key);
    const row: TextRow = { id: `dish:${d.id}`, group: d.kind === "drink" ? "drink" : "dish", dish: d, spots: own, langs: {} };
    if (src.proofread) {
      const de = germanState(own, src.proofs);
      if (de) row.de = de;
    }
    for (const l of langs) {
      const st = dishLangState(d, l);
      if (st) row.langs[l] = st;
    }
    rows.push(row);
  }
  // The other texts once each, wherever they stand: from the dictionary and from the spots.
  const texts = new Map<string, { group: PhraseGroup; spots: ProofSpot[] }>();
  for (const [text, group] of words) texts.set(text, { group, spots: [] });
  for (const s of spots) {
    if (counted.has(s.key) || !s.text.trim()) continue;
    const key = phraseKey(s.text);
    const at = texts.get(key);
    if (at) at.spots.push(s);
    else texts.set(key, { group: groupOf(s.key), spots: [s] });
  }
  for (const [text, { group, spots: at }] of texts) {
    const row: TextRow = { id: `text:${text}`, group, text, spots: at, langs: {} };
    if (src.proofread) {
      const de = germanState(at, src.proofs);
      if (de) row.de = de;
    }
    if (words.has(text)) {
      for (const l of langs) {
        const st = phraseLangState(text, l, src.phrases);
        if (st) row.langs[l] = st;
      }
    }
    if (row.de || Object.keys(row.langs).length) rows.push(row);
  }
  const targets: TextTarget[] = [...(src.proofread ? (["de"] as const) : []), ...langs];
  const counts: Partial<Record<TextTarget, TargetCounts>> = {};
  for (const t of targets) {
    const c: TargetCounts = { open: { dishes: 0, texts: 0 }, review: { dishes: 0, texts: 0 }, stale: { dishes: 0, texts: 0 } };
    for (const r of rows) {
      const st = t === "de" ? r.de : r.langs[t];
      if (!st || st === "ok") continue;
      c[st][r.dish ? "dishes" : "texts"]++;
    }
    counts[t] = c;
  }
  return { rows, targets, counts };
}

/** The state of a row for a target. */
export const rowState = (r: TextRow, t: TextTarget): TextState | undefined => (t === "de" ? r.de : r.langs[t]);

/** How many rows of a target are in a state, dishes and texts together. */
export const countOf = (c: TargetCounts | undefined, state: Exclude<TextState, "ok">) => (c ? c[state].dishes + c[state].texts : 0);

/** „12 Speisen und Getränke und 3 Texte“; „Speisen“ alone when there are no drinks among them is not known here. */
export function countText(c: StateCount): string {
  const dishes = c.dishes === 1 ? "1 Speise oder Getränk" : `${c.dishes} Speisen und Getränke`;
  const texts = c.texts === 1 ? "1 Text" : `${c.texts} Texte`;
  return [c.dishes ? dishes : "", c.texts ? texts : ""].filter(Boolean).join(" und ");
}
